/**
 * Singleton keyboard shortcut manager
 *
 * Owns the single document-level keydown listener that all shortcuts
 * dispatch through. Components register a `(spec, handler)` pair via
 * `useShortcut` (which calls `registerShortcut` under the hood); the
 * manager scans its current registrations on every keystroke and fires
 * the first matching handler.
 *
 * Why a singleton:
 *   - One DOM listener, one place that owns `preventDefault` /
 *     `stopPropagation` / capture-phase wiring / editable-target gating.
 *     The previous design had three hand-rolled hooks each repeating
 *     this boilerplate.
 *   - Centralized introspection — `getRegistrations` / `subscribeRegistrations`
 *     back a future cheatsheet UI that lists every currently-active
 *     shortcut.
 *   - Centralized conflict detection — registering two specs that bind
 *     to the same keystroke logs a dev-mode warning.
 *
 * Match precedence: registrations are scanned in reverse insertion order
 * (LIFO). The most recently mounted shortcut wins ties. In practice the
 * viewer doesn't have duplicate bindings, so this only matters if a future
 * scope/modal layer wants to override an outer binding.
 */
// ---------------------------------------------------------------------------
// Module state
// ---------------------------------------------------------------------------
let _nextId = 1;
// Reference identity changes on every register / unregister so
// `useSyncExternalStore` re-renders subscribers correctly. Splice-mutation
// would keep the same array reference and miss updates.
let _registrations = [];
const _subscribers = new Set();
let _listenerInstalled = false;
// `navigator.platform` is technically deprecated but remains the most
// portable Mac detector in evergreen browsers. The newer
// `navigator.userAgentData` isn't universally available yet (notably
// missing in Safari and Firefox stable). Detection happens once at module
// load — there's no realistic way for the same session to switch
// platforms.
const IS_MAC = typeof navigator !== "undefined" &&
    /Mac|iPhone|iPad|iPod/.test(navigator.platform);
// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function isEditableTarget(target) {
    if (!(target instanceof HTMLElement))
        return false;
    if (target.isContentEditable)
        return true;
    const tag = target.tagName;
    return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT";
}
function parseKeys(keys) {
    let hasMod = false;
    let hasShift = false;
    let hasAlt = false;
    let key = "";
    for (const k of keys) {
        if (k === "Mod")
            hasMod = true;
        else if (k === "Shift")
            hasShift = true;
        else if (k === "Alt")
            hasAlt = true;
        else
            key = k.toLowerCase();
    }
    return { hasMod, hasShift, hasAlt, key };
}
function eventMatches(e, parsed) {
    // Mod = Cmd on Mac, Ctrl elsewhere. Reject the *other* platform's Mod
    // key explicitly so a Ctrl+E on Mac doesn't accidentally trigger a Mod+E
    // shortcut and vice versa.
    const eventModActive = IS_MAC ? e.metaKey : e.ctrlKey;
    const oppositeModActive = IS_MAC ? e.ctrlKey : e.metaKey;
    if (parsed.hasMod) {
        if (eventModActive === false)
            return false;
        if (oppositeModActive)
            return false;
    }
    else {
        if (eventModActive || oppositeModActive)
            return false;
    }
    // Strict Shift / Alt matching: ["E"] only fires for plain E, never
    // Shift+E. ["Shift", "E"] only fires for Shift+E. This makes ⌘E and
    // ⌘+Shift+E unambiguously distinct.
    if (parsed.hasShift !== e.shiftKey)
        return false;
    if (parsed.hasAlt !== e.altKey)
        return false;
    // Key matching is case-insensitive. Caps Lock + plain E reports
    // `e.key === "E"` with `shiftKey: false`, which our lowercase compare
    // still treats as a match — matching the user's mental model of "I
    // pressed E without Shift".
    return e.key.toLowerCase() === parsed.key;
}
function notifySubscribers() {
    for (const fn of _subscribers)
        fn();
}
function detectConflict(spec) {
    const parsed = parseKeys(spec.keys);
    for (const reg of _registrations) {
        if (reg.spec.id === spec.id)
            continue;
        const other = parseKeys(reg.spec.keys);
        if (parsed.hasMod === other.hasMod &&
            parsed.hasShift === other.hasShift &&
            parsed.hasAlt === other.hasAlt &&
            parsed.key === other.key &&
            // Two specs that disagree on `allowInInputs` aren't strictly
            // colliding — one fires only outside inputs, the other inside —
            // so don't warn in that case.
            (reg.spec.allowInInputs ?? false) === (spec.allowInInputs ?? false)) {
            return reg;
        }
    }
    return undefined;
}
// ---------------------------------------------------------------------------
// Listener
// ---------------------------------------------------------------------------
function dispatch(e) {
    // Reverse iteration: most-recently registered wins ties (LIFO).
    for (let i = _registrations.length - 1; i >= 0; i--) {
        const reg = _registrations[i];
        if (reg === undefined)
            continue;
        const parsed = parseKeys(reg.spec.keys);
        if (eventMatches(e, parsed) === false)
            continue;
        if (reg.spec.allowInInputs !== true &&
            isEditableTarget(e.target)) {
            continue;
        }
        if (reg.enabled !== undefined && reg.enabled() === false)
            continue;
        e.preventDefault();
        e.stopPropagation();
        reg.handler(e);
        return;
    }
}
function ensureListener() {
    if (_listenerInstalled)
        return;
    if (typeof document === "undefined")
        return;
    _listenerInstalled = true;
    document.addEventListener("keydown", dispatch, { capture: true });
}
// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------
export function registerShortcut(reg) {
    ensureListener();
    if (process.env.NODE_ENV !== "production") {
        const conflict = detectConflict(reg.spec);
        if (conflict !== undefined) {
            // eslint-disable-next-line no-console -- dev-only diagnostic
            console.warn(`[shortcut-manager] keys collision: "${reg.spec.id}" and "${conflict.spec.id}" both bind to ${reg.spec.keys.join("+")}`);
        }
    }
    const internalId = _nextId++;
    const internal = { internalId, ...reg };
    _registrations = [..._registrations, internal];
    notifySubscribers();
    return () => {
        _registrations = _registrations.filter((r) => r.internalId !== internalId);
        notifySubscribers();
    };
}
/**
 * Snapshot of the currently-active registrations. Returns the same
 * reference until something registers / unregisters, so it's safe to use
 * as the snapshot for `useSyncExternalStore`.
 */
export function getRegistrations() {
    return _registrations;
}
export function subscribeRegistrations(fn) {
    _subscribers.add(fn);
    return () => {
        _subscribers.delete(fn);
    };
}
